import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { useShipmentPageContext, useWriterComponentState } from "hooks";

import { getSimpleQuoteInteractionConfig } from "./utils";
import { ClientAppEvent } from "../../../../../../constants";

import type { SimpleQuoteInteractionType } from "./utils";

interface Props {
  quoteId: number;
  onClose: VoidFunction;
  type: SimpleQuoteInteractionType;
}

export function SimpleQuoteInteraction(props: Props) {
  const { quoteId, onClose, type } = props;
  const { shipment, onRefresh } = useShipmentPageContext();
  const posthog = usePostHog();

  const { actionUrl, contentText, title, actionButtonText, actionButtonColor } =
    getSimpleQuoteInteractionConfig(quoteId, type);

  const { loading, error, write } = useWriterComponentState({
    errorMessage: `Failed to ${type.toLowerCase()} quote`,
    writeConfig: {
      method: "post",
      url: actionUrl,
    },
  });

  const captureInteractionEvent = () => {
    const shipmentId = shipment?.info?.id;
    if (type === "ACCEPT") {
      posthog?.capture(ClientAppEvent.QUOTE_ACCEPTED, {
        quote_id: quoteId,
        shipment_id: shipmentId,
      });
    } else if (type === "DECLINE") {
      posthog?.capture(ClientAppEvent.QUOTE_DECLINED, {
        quote_id: quoteId,
        shipment_id: shipmentId,
      });
    } else if (type === "CANCEL") {
      posthog?.capture(ClientAppEvent.QUOTE_CANCELLED, {
        quote_id: quoteId,
        shipment_id: shipmentId,
        cancelled_by_role: "SHIPPER",
      });
    }
  };

  const onConfirmAction = () => {
    write(() => {
      captureInteractionEvent();
      onRefresh?.();
      onClose();
    });
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Typography variant="subtitle1">{contentText}</Typography>
        )}
        {error != null && !loading && (
          <Typography color="red" align="center">
            {error}
          </Typography>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button autoFocus onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionButtonColor}
            onClick={onConfirmAction}
          >
            {actionButtonText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
